import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import moment from 'moment/moment';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextBoxController } from '@components-clean/core/text-box';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { DataFilter, FieldType, FilterForm } from '@pages-clean/admin/study/admin-data-views/filters/types';
import { isEmptyObjectId, isValidObjectId } from '@utils-clean/validation';
import { RowDataSourceTable } from 'types/graphql-global-types';
import { IUser } from '@contexts/user-context';
import Constants from '@utils/constants';

type AddDataFilterDialogProps = {
    user: IUser;
    onClose: () => void;
    existingFilters?: DataFilter[];
    fields: FieldType[];
    selectedDataSource: RowDataSourceTable | undefined | null;
    type: 'add' | 'edit';
    dataItem?: DataFilter;
    submit: (data: FilterForm) => void;
    nonRemovableFields?: string[];
};

const COMPONENT_NAME = 'add-data-filter-dialog';

export const AddDataFilterDialog = ({
    user,
    onClose,
    existingFilters,
    fields,
    selectedDataSource,
    type,
    dataItem,
    submit,
    nonRemovableFields,
}: AddDataFilterDialogProps) => {
    const { t } = useTranslation();
    const isSysAdmin = user.systemAdminLevel === Constants.SystemAdminLevel.SysAdmin;
    const canEdit = user.canWriteSystemAdminLevel?.() ?? false;
    const isSelectedFieldActive = fields?.find((f) => f.key === dataItem?.fieldKey)?.active || false;
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const {
        control,
        handleSubmit,
        formState: { isDirty, errors },
        trigger,
    } = useForm<FilterForm>({
        defaultValues: {
            label: type === 'edit' ? dataItem?.label : '',
            field:
                type === 'edit'
                    ? {
                          key: dataItem?.fieldKey,
                          name: dataItem?.fieldName,
                          active: isSelectedFieldActive,
                          status: fields.find((f) => f.key === dataItem?.fieldKey)?.status,
                      }
                    : undefined,
            configId: type === 'edit' ? dataItem?.configId : '',
        },

        mode: 'all',
    });

    useEffect(() => {
        if (isDirty) trigger().catch((err) => window.console.error(err));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [existingFilters]);

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isDirty]);

    const onCloseHandler = async () => {
        if (componentHasChanges(COMPONENT_NAME)) {
            const confirmed = await confirmDiscardChanges('add-data-filter-confirmation');
            if (!confirmed) {
                return;
            }
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        onClose();
    };
    const isValid = Object.keys(errors).length === 0;
    const isFieldReadOnly =
        dataItem?.dataSource === RowDataSourceTable.SUBJECT || dataItem?.dataSource === RowDataSourceTable.SITE
            ? nonRemovableFields?.includes(dataItem?.fieldKey)
            : !!dataItem?.isDefault;

    const createdBy = `${moment(dataItem?.created).format('DD-MMM-YYYY')} by ${dataItem?.createdByFullName}`;
    const updatedBy = `${moment(dataItem?.updated).format('DD-MMM-YYYY')} by ${dataItem?.updatedByFullName}`;

    return (
        <DraggableDialog
            dataTestId="add-data-filter-dialog"
            title={
                type === 'add'
                    ? t('admin:filters.addDataFilter')
                    : `${t('admin:filters.editDataFilter')}: ${dataItem?.recordId}`
            }
            primaryBtnLabel={type === 'add' ? t('add') : `${t('update')}`}
            createdUpdatedInfo={type === 'edit' && <CreatedUpdatedInfo created={createdBy} updated={updatedBy} />}
            secondaryBtnLabel={t('cancel')}
            onSubmit={() => handleSubmit(submit)()}
            onClose={onCloseHandler}
            primaryBtnDisabled={!isValid || !isDirty}
            height={type === 'add' ? 220 : 320}
            initialWidth={790}
        >
            <form id="add-data-filter-form" className="k-w-full k-d-flex k-flex-column k-gap-2">
                <BasicDropDownController
                    control={control}
                    data={
                        fields
                            .filter(
                                (item) =>
                                    item?.dataType === selectedDataSource &&
                                    !existingFilters?.find((x) => x.id !== dataItem?.id && x?.fieldKey === item.key),
                            )
                            .map((f) => {
                                return {
                                    name: f.name,
                                    key: f.key,
                                    status: f.status,
                                    active: f.active,
                                };
                            }) || []
                    }
                    name="field"
                    data-testid="data-filter-field"
                    label={`${t('admin:filters.field')}*`}
                    textField="name"
                    dataItemKey="key"
                    required
                    readOnly={(type === 'edit' && isFieldReadOnly) || !canEdit}
                    groupField="status"
                    activeField="active"
                    filterable
                    allowClear
                />

                <TextBoxController
                    control={control}
                    name="label"
                    data-testid="data-filter-field"
                    label={`${t('admin:filters.label')}*`}
                    maxLength={30}
                    required
                    readOnly={!canEdit}
                    validateRules={{
                        uniqueValue: (value) => {
                            if (
                                value &&
                                existingFilters?.find(
                                    (x) =>
                                        x.id !== dataItem?.id &&
                                        x.label.toLocaleLowerCase().trim() ===
                                            (value as string).toLocaleLowerCase().trim(),
                                )
                            )
                                return i18next.t('admin:filters.validation.uniqueLabel');
                            return true;
                        },
                        whitespace: (value) => {
                            if (value && !(value as string).trim().length) return i18next.t('ra:requiredFieldError');
                        },
                    }}
                />
                {type === 'edit' && (
                    <TextBoxController
                        control={control}
                        name="configId"
                        data-testid="data-filter-config-id"
                        label={t('admin:configId')}
                        value={dataItem?.configId}
                        readOnly={!isSysAdmin}
                        copyBtn
                        required
                        validateRules={{
                            isObjectId: (value) => {
                                if (
                                    isSysAdmin &&
                                    (!isValidObjectId(value as string) || isEmptyObjectId(value as string))
                                )
                                    return t('admin:validConfigIdError');
                            },
                            uniqueValue: (value) => {
                                if (
                                    isSysAdmin &&
                                    value &&
                                    existingFilters?.find(
                                        (x) =>
                                            x.id !== dataItem?.id &&
                                            x.configId.toLocaleLowerCase().trim() ===
                                                (value as string).toLocaleLowerCase().trim(),
                                    )
                                )
                                    return i18next.t('admin:uniqueConfigIdError');
                            },
                        }}
                    />
                )}
            </form>
        </DraggableDialog>
    );
};
